import React, { useEffect, useRef, useState } from "react";
import Hls from "hls.js"; // Importa la librería hls.js para manejar la reproducción de HLS

// Define la interfaz para las propiedades del componente HlsPlayer
interface HlsPlayerProps {
    src: string; // URL del archivo HLS (.m3u8)
    width?: string; // Ancho del video (opcional, valor por defecto: "100%")
    height?: string; // Alto del video (opcional, valor por defecto: "100%")
    autoPlay?: boolean; // Si el video debe reproducirse automáticamente (opcional, valor por defecto: true)
    controls?: boolean; // Mostrar controles del reproductor (opcional, valor por defecto: true)
}

// Define el componente HlsPlayer como un componente funcional de React
const HlsPlayer: React.FC<HlsPlayerProps> = ({
    src,
    width = "100%",
    height = "100%",
    autoPlay = true,
    controls = true,
}) => {
    // Estado para manejar si el video está cargando
    const [isLoading, setIsLoading] = useState(true);
    // Estado para manejar errores durante la carga del video
    const [error, setError] = useState<string | null>(null);
    // Referencia al elemento de video del DOM
    const videoRef = useRef<HTMLVideoElement | null>(null);

    // Función para habilitar la reproducción automática después de la interacción del usuario
    const enableAutoplay = () => {
        videoRef.current?.play().catch((error) => console.error("Error al reproducir", error));
        document.removeEventListener("click", enableAutoplay); // Elimina el listener después de la interacción
    };

    // Función para verificar si la URL del archivo .m3u8 es válida
    const checkM3U8Url = async (url: string) => {
        try {
            const response = await fetch(url, { method: "HEAD" }); // Realiza una solicitud HEAD para verificar la URL
            if (!response.ok) {
                throw new Error(`Archivo no encontrado: ${url}`); // Lanza un error si la URL no es válida
            }
            return true; // Retorna true si la URL es válida
        } catch (error) {
            console.error("Error al verificar la URL:", error); // Maneja errores de red o de URL
            return false; // Retorna false si hay un error
        }
    };

    // Efecto para inicializar el reproductor HLS cuando el componente se monta o cambia la URL
    useEffect(() => {
        if (!videoRef.current) return; // Si no hay referencia al video, no hacer nada

        const video = videoRef.current; // Obtiene la referencia al elemento de video
        let hls: Hls;
        let bufferCheckInterval: NodeJS.Timeout;
        const initializePlayer = async () => {
            const isUrlValid = await checkM3U8Url(src); // Verifica si la URL del archivo .m3u8 es válida
            if (!isUrlValid) {
                setError("El archivo de video no está disponible."); // Muestra un mensaje de error si la URL no es válida
                setIsLoading(false); // Cambia el estado de carga a falso
                return; // Detiene la ejecución si la URL no es válida
            }

            // Verifica si el navegador soporta hls.js
            if (Hls.isSupported()) {
                hls = new Hls({
                    // debug: true, // Habilita logs de depuración
                    liveDurationInfinity: true, // Permite duración infinita para streams en vivo
                    maxBufferLength: 120, // Tamaño máximo del buffer en segundos
                    maxMaxBufferLength: 240, // Tamaño máximo absoluto del buffer
                    maxBufferSize: 120 * 1000 * 1000, // Tamaño máximo del buffer en bytes
                    maxBufferHole: 0.5, // Tolerancia para saltos en el stream
                    highBufferWatchdogPeriod: 2, // Frecuencia de verificación del buffer
                    liveSyncDuration: 5, // Duración de sincronización para streams en vivo
                });
                hls.loadSource(src); // Carga la URL del archivo .m3u8
                hls.attachMedia(video); // Asocia el video al reproductor HLS

                // Evento cuando el manifiesto HLS es cargado y parseado
                hls.on(Hls.Events.MANIFEST_PARSED, () => {
                    console.log("HLS manifest cargado");
                    setIsLoading(false); // Cambia el estado de carga a falso
                    // Comenzar a reproducir el video
                    video.play().catch((error) => {
                        console.error("Error al reproducir automáticamente:", error);
                    });
                });

                hls.on(Hls.Events.ERROR, (event, data) => {
                    console.error("HLS error", data);
                    if (data.type === Hls.ErrorTypes.NETWORK_ERROR || data.type === Hls.ErrorTypes.MEDIA_ERROR) {
                        console.warn("Error de red o medios, intentando recuperar...");
                        hls.loadSource(src)
                        hls.startLoad(); // Reinicia la carga del stream
                    }
                });
                // Verificar el búfer cada segundo
                bufferCheckInterval = setInterval(() => {
                    if (video.buffered.length > 0) {
                        const bufferedEnd = video.buffered.end(video.buffered.length - 1);
                        const currentTime = video.currentTime;
                        if (bufferedEnd - currentTime < 2) { // Si el búfer es menor a 2 segundos
                            console.warn("Búfer bajo, intentando recuperar...");
                            // hls.loadSource(src)
                            hls.startLoad(); // Reinicia la carga del stream
                            // video.play().catch(err => console.error("Error al reanudar la reproducción:", err));
                        }
                    } else {
                        console.warn("No hay datos en el buffer, recargando...");
                        // hls.startLoad();
                    }
                }, 1000);
                // Limpieza al desmontar el componente
                return () => {
                    clearInterval(bufferCheckInterval);
                    hls.destroy(); // Destruye la instancia de HLS
                    document.removeEventListener("click", enableAutoplay); // Elimina el listener de clic
                };
            } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
                // Si el navegador no soporta hls.js pero soporta HLS nativo (Safari)
                video.src = src; // Asigna la URL directamente al elemento de video
                setIsLoading(false); // Cambia el estado de carga a falso
                // Comenzar a reproducir el video
                video.play().catch((error) => {
                    console.error("Error al reproducir automáticamente:", error);
                });
            } else {
                // Si el navegador no soporta HLS
                setError("Tu navegador no soporta la reproducción de HLS."); // Muestra un mensaje de error
                setIsLoading(false); // Cambia el estado de carga a falso
            }
        };

        initializePlayer(); // Inicializa el reproductor
    }, [src]); // Ejecuta el efecto cuando cambia la URL del video

    return (
        <div style={{ width: "100vw", height: "100vh", margin: 0, padding: 0, overflow: "hidden" }}>
            {/* Muestra un mensaje de carga */}
            {isLoading && <p>Cargando video...</p>}
            {/* Muestra un mensaje de error si hay alguno */}
            {error && <p>{error}</p>}
            <video
                ref={videoRef}
                autoPlay
                controls={controls} // Muestra los controles del reproductor si está habilitado
                muted // Silencia el video para permitir autoplay en navegadores modernos
                style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover", // Ajusta el video para cubrir el contenedor
                    display: isLoading || error ? "none" : "block", // Oculta el video si está cargando o hay un error
                }}
            />
        </div>
    );
};

export default HlsPlayer;